import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "rulesContainer", 
    "rulesDisplay", 
    "ruleTemplate",
    "hiddenFieldsTemplate",
    "configurationTypeSelect",
    "assetLayoutSelect", 
    "existingLayoutSelect",
    "newLayoutInput"
  ];
  
  static values = {
    rules: Array,
    defaultConfigType: String,
    defaultAssetLayout: String,
    fieldName: String
  };

  connect() {
    this.ruleCounter = this.rulesValue.length;
    this.rulesValue.forEach((ruleData, index) => {
      this.createRuleFromTemplate(index, ruleData);
    });
  }

  addRule(event) {
    event.preventDefault();

    // Get default values from Stimulus data attributes
    const defaultConfigTypeId = this.defaultConfigTypeValue;
    const defaultAssetLayoutId = this.defaultAssetLayoutValue;
    
    if (!defaultConfigTypeId || !defaultAssetLayoutId) {
      alert("No configuration types or asset layouts available");
      return;
    }

    const newIndex = this.ruleCounter++;
    
    // Create rule data once and reuse
    const ruleData = {
      id: null,
      configuration_type_id: defaultConfigTypeId,
      asset_layout_id: defaultAssetLayoutId,
      new_asset_layout_name: "",
      _destroy: false
    };
    
    this.createHiddenFields(newIndex, ruleData);
    this.createRuleFromTemplate(newIndex, ruleData);
  }

  removeRule(event) {
    event.preventDefault();
    
    if (!confirm("Are you sure?")) {
      return;
    }
    
    const ruleIndex = event.target.dataset.ruleIndex;
    
    // Use helper method to find hidden field
    const destroyField = this.findHiddenField(ruleIndex, "_destroy");
    if (destroyField) {
      destroyField.value = "true";
    }
    
    // Use helper method to find rule section
    const ruleSection = this.findRuleSection(ruleIndex);
    if (ruleSection) {
      ruleSection.style.display = "none";
      ruleSection.dataset.ruleDestroyed = "true";
    }
  }

  toggleNewName(event) {
    event.preventDefault();
    
    const ruleIndex = event.target.dataset.ruleIndex;
    const existingLayoutDiv = this.findRuleTarget("existingLayoutSelect", ruleIndex);
    const newLayoutDiv = this.findRuleTarget("newLayoutInput", ruleIndex);
    
    existingLayoutDiv.style.display = "none";
    newLayoutDiv.style.display = "block";
  }

  removeNewName(event) {
    event.preventDefault();
    
    const ruleIndex = event.target.dataset.ruleIndex;
    const existingLayoutDiv = this.findRuleTarget("existingLayoutSelect", ruleIndex);
    const newLayoutDiv = this.findRuleTarget("newLayoutInput", ruleIndex);
    const newNameInput = newLayoutDiv.querySelector('input');
    
    existingLayoutDiv.style.display = "block";
    newLayoutDiv.style.display = "none";
    
    newNameInput.value = "";
    this.updateHiddenField(ruleIndex, "new_asset_layout_name", "");
    
    // Set to first available asset layout
    const select = existingLayoutDiv.querySelector('select');
    if (select && select.options.length > 0) {
      const firstOption = select.options[0];
      select.value = firstOption.value;
      this.updateHiddenField(ruleIndex, "asset_layout_id", firstOption.value);
    }
  }

  updateConfigurationType(event) {
    const ruleIndex = event.target.dataset.ruleIndex;
    const value = event.target.value;
    this.updateHiddenField(ruleIndex, "configuration_type_id", value);
  }

  updateAssetLayout(event) {
    const ruleIndex = event.target.dataset.ruleIndex;
    const value = event.target.value;
    this.updateHiddenField(ruleIndex, "asset_layout_id", value);
  }

  updateNewAssetLayoutName(event) {
    const ruleIndex = event.target.dataset.ruleIndex;
    const value = event.target.value;
    this.updateHiddenField(ruleIndex, "new_asset_layout_name", value);
  }

  createHiddenFields(index, ruleData) {
    const template = this.hiddenFieldsTemplateTarget;
    const clone = template.content.cloneNode(true);
    
    const container = clone.querySelector('div');
    this.updateTemplateIndexes(container, index);
    
    // Set values for the hidden fields
    const fields = ['id', 'configuration_type_id', 'asset_layout_id', '_destroy', 'new_asset_layout_name'];
    fields.forEach(field => {
      const input = container.querySelector(`input[name*="[${field}]"]`);
      if (input) {
        input.value = ruleData[field] || '';
      }
    });
    
    this.rulesContainerTarget.appendChild(container);
  }

  createRuleFromTemplate(index, ruleData) {
    const template = this.ruleTemplateTarget;
    const clone = template.content.cloneNode(true);
    
    const section = clone.querySelector('section');
    this.updateTemplateIndexes(section, index);
    
    // Add to DOM first so we can use targets
    this.rulesDisplayTarget.appendChild(section);
    
    // Set selected values for dropdowns
    const configSelect = this.findRuleTarget("configurationTypeSelect", index);
    const assetLayoutSelect = this.findRuleTarget("assetLayoutSelect", index);
    
    if (configSelect && ruleData.configuration_type_id) {
      configSelect.value = ruleData.configuration_type_id;
    }
    if (assetLayoutSelect && ruleData.asset_layout_id) {
      assetLayoutSelect.value = ruleData.asset_layout_id;
    }
    
    if (ruleData.new_asset_layout_name) {
      const existingDiv = this.findRuleTarget("existingLayoutSelect", index);
      const newDiv = this.findRuleTarget("newLayoutInput", index);
      const input = newDiv.querySelector('input');
      
      existingDiv.style.display = "none";
      newDiv.style.display = "block";
      input.value = ruleData.new_asset_layout_name;
    }
  }

  // Helper method to find rule targets by index (reduces repetition)
  findRuleTarget(targetName, ruleIndex) {
    const targets = this[`${targetName}Targets`];
    return targets.find(target => target.dataset.ruleIndex === ruleIndex.toString());
  }

  // Helper method to find rule sections by index
  findRuleSection(ruleIndex) {
    return this.rulesDisplayTarget.querySelector(`[data-rule-index="${ruleIndex}"]`);
  }

  // Helper method to find hidden fields by index and field name
  findHiddenField(ruleIndex, fieldName) {
    return this.rulesContainerTarget.querySelector(
      `input[name="integrator[${this.fieldNameValue}][${ruleIndex}][${fieldName}]"]`
    );
  }

  updateTemplateIndexes(element, index) {
    // Update data-rule-index attributes
    const elementsWithIndex = element.querySelectorAll('[data-rule-index="TEMPLATE_INDEX"]');
    elementsWithIndex.forEach(el => {
      el.dataset.ruleIndex = index;
    });
    if (element.dataset && element.dataset.ruleIndex === "TEMPLATE_INDEX") {
      element.dataset.ruleIndex = index;
    }
    
    // Update hidden field name attributes
    const hiddenInputs = element.querySelectorAll('input[name*="TEMPLATE_INDEX"]');
    hiddenInputs.forEach(input => {
      input.name = input.name.replace(/TEMPLATE_INDEX/g, index);
    });
  }

  updateHiddenField(ruleIndex, fieldName, value) {
    const field = this.findHiddenField(ruleIndex, fieldName);
    if (field) {
      field.value = value;
    }
  }
} 